import { CMSNotification, useNotificationStore, useSocketConnected, useSockets, useUserStore } from '@editor/ui-utils';
import type { Socket } from 'socket.io-client';
import { onScopeDispose, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

/**
 * How long the "test finished" banner stays up before dismissing itself.
 *
 * The banner was previously persistent (`timeout: null`), which meant it sat there until someone
 * clicked it - including on machines nobody was watching. One named constant so the duration is
 * changed in a single place rather than at the call site.
 */
const TEST_COMPLETED_NOTIFICATION_TIMEOUT_MS = 30_000;

/**
 * Editors routinely keep a dozen Vibe tabs open, and every connected tab receives the same
 * `testCompleted` socket event - so the same banner appears in all of them. Dismissing it once
 * should clear it everywhere rather than leaving the user to close it tab by tab.
 *
 * Deliberately live-only: `BroadcastChannel` reaches the tabs open right now, which is all that is
 * needed because the socket event never reaches a tab opened later either. Nothing is persisted, so
 * there is no dismissed-id record to expire or clean up.
 */
const DISMISS_CHANNEL = 'ab-test-notifications';

interface IDismissMessage {
  notificationId: string;
}

interface IAbTestCompletedEvent {
  testId: string;
  contentUuid: string;
  createdByUserUuid?: string;
  status: 'completed' | 'completed_no_winner';
  articleTitle?: string;
}

interface IServerToClientEvents {
  testCompleted: (event: IAbTestCompletedEvent) => void;
}

export default function useAbTestNotifications(): void {
  const { t } = useI18n();
  const router = useRouter();
  const userStore = useUserStore();
  const { showNotification, hideNotification, isNotificationVisible } = useNotificationStore();

  // `BroadcastChannel` is absent in some test environments; without it the banner simply behaves
  // per-tab as before rather than breaking.
  const dismissChannel: BroadcastChannel | undefined =
    typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(DISMISS_CHANNEL);

  /**
   * Ids currently being closed because another tab asked us to. `onDismiss` fires for a programmatic
   * hide too, so without this guard each tab would echo the message back and the tabs would bounce
   * it around between them.
   */
  const dismissedRemotely = new Set<string>();

  const dismissFromOtherTab = (notificationId: string): void => {
    if (!isNotificationVisible(notificationId)) return;
    dismissedRemotely.add(notificationId);
    try {
      hideNotification(notificationId);
    } finally {
      dismissedRemotely.delete(notificationId);
    }
  };

  if (dismissChannel) {
    dismissChannel.onmessage = (event: MessageEvent<IDismissMessage>): void => {
      const notificationId = event.data?.notificationId;
      if (notificationId) dismissFromOtherTab(notificationId);
    };
    onScopeDispose(() => dismissChannel.close());
  }

  /**
   * When each banner is due to disappear, keyed by notification id.
   *
   * The deadline belongs to the event, not to the tab: every tab receives the same socket message,
   * so they all expire together and a dismissal in one is meaningful in the others.
   */
  const deadlines = new Map<string, number>();
  /** The event behind each live banner, so it can be rebuilt verbatim. */
  const liveEvents = new Map<string, IAbTestCompletedEvent>();
  /** Ids being re-created right now, so a rebuild is not mistaken for a dismissal. */
  const rebuilding = new Set<string>();

  const showTestCompleted = (event: IAbTestCompletedEvent, remainingMs: number): void => {
    const baseMessage = event.status === 'completed' ? t('abTesting.testCompletedWithWinner') : t('abTesting.testCompletedNoWinner');
    const escapedTitle = event.articleTitle?.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // Styles are inlined (not in a class) so they apply regardless of where the
    // notification stylesheet is loaded. The heading (test-finished message) is
    // bold — that's the primary info; the "Article: <title>" line below is normal
    // weight (secondary). `min-width: 0` on the title lets the flex item shrink so
    // the ellipsis actually triggers on long titles instead of wrapping.
    const heading = `<span style="font-weight:700;">${baseMessage}</span>`;
    const message = escapedTitle
      ? `${heading}<br>` +
        '<span class="ab-testing__article" style="display:flex;width:500px;max-width:100%;gap:4px;">' +
        `<span class="ab-testing__article-label" style="flex:0 0 auto;">${t('abTesting.articleTitle')}:</span>` +
        `<span class="ab-testing__article-title" style="flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapedTitle}</span>` +
        '</span>'
      : heading;

    showNotification(
      new CMSNotification(message, 'positive', {
        id: `ab-test-completed-${event.testId}`,
        // Applies to both outcomes: `handleTestCompleted` serves 'completed' and
        // 'completed_no_winner' alike, so neither can be left hanging on screen.
        timeout: remainingMs,
        dismissible: true,
        // Now that the banner expires, show the countdown - otherwise it just vanishes unannounced.
        progress: true,
        multiLine: true,
        html: true,
        // Covers every way the banner can go: the close button, the timeout, and the action
        // below - so a dismissal in any tab clears it in all of them.
        onDismiss() {
          const notificationId = `ab-test-completed-${event.testId}`;
          // `rebuilding` covers the re-create below: that is not the banner going away, so neither
          // the deadline nor the other tabs should hear about it.
          if (rebuilding.has(notificationId)) return;
          deadlines.delete(notificationId);
          liveEvents.delete(notificationId);
          if (dismissedRemotely.has(notificationId)) return;
          dismissChannel?.postMessage({ notificationId } satisfies IDismissMessage);
        },
        callbackLabel: t('abTesting.viewResults'),
        // A real link rather than a click handler, so the browser's own affordances work: right-click
        // "open in new tab", middle-click and cmd-click all need an anchor with an href. A button
        // gives the context menu nothing to act on, which is what editors noticed.
        callbackHref: router.resolve({ name: 'ab_test_history', query: { contentUuid: event.contentUuid } }).href,
        callbackTarget: '_blank',
      }),
    );
  };

  const handleTestCompleted = (event: IAbTestCompletedEvent): void => {
    if (!event.createdByUserUuid || event.createdByUserUuid !== userStore.userId) {
      return;
    }

    const notificationId = `ab-test-completed-${event.testId}`;
    deadlines.set(notificationId, Date.now() + TEST_COMPLETED_NOTIFICATION_TIMEOUT_MS);
    liveEvents.set(notificationId, event);
    showTestCompleted(event, TEST_COMPLETED_NOTIFICATION_TIMEOUT_MS);
  };

  /**
   * Re-times the countdown when a hidden tab is brought forward.
   *
   * A browser does not paint a hidden tab, so its progress bar has no way to show time that passed
   * while it was in the background: it starts from zero on first paint and the banner then vanishes
   * partway along it. Rebuilding the banner with only the time actually left makes the bar and the
   * disappearance agree again. The deadline itself never moves - switching tabs cannot buy the
   * banner more time, which would also break dismissing it everywhere at once.
   */
  const retimeVisibleNotifications = (): void => {
    if (document.visibilityState !== 'visible') return;

    for (const [notificationId, deadline] of [...deadlines]) {
      const event = liveEvents.get(notificationId);
      if (!event || !isNotificationVisible(notificationId)) {
        deadlines.delete(notificationId);
        liveEvents.delete(notificationId);
        continue;
      }

      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        hideNotification(notificationId);
        deadlines.delete(notificationId);
        liveEvents.delete(notificationId);
        continue;
      }

      rebuilding.add(notificationId);
      try {
        hideNotification(notificationId);
        showTestCompleted(event, remaining);
      } finally {
        rebuilding.delete(notificationId);
      }
    }
  };

  document.addEventListener('visibilitychange', retimeVisibleNotifications);
  onScopeDispose(() => document.removeEventListener('visibilitychange', retimeVisibleNotifications));

  let channel: Socket<IServerToClientEvents> | undefined;

  const connect = (isConnected?: boolean): void => {
    if (!isConnected) return;
    if (channel) {
      channel.off('testCompleted', handleTestCompleted);
    }
    const io = useSockets();
    channel = io.socket('/ab-testing');
    channel.on('testCompleted', handleTestCompleted);
  };

  watch(useSocketConnected(), connect, { immediate: true });
}
